import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

const STORAGE_KEY = 'xenicos-theme';
const VALID = ['light', 'dark', 'system'];

const computeResolved = (pref) => {
  if (pref === 'light' || pref === 'dark') return pref;
  // system
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  return 'dark';
};

export const ThemeProvider = ({ children }) => {
  // `preference` is the user-selected value (light|dark|system)
  // `resolved` is the actual applied theme (light|dark)
  const [preference, setPreferenceState] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      return VALID.includes(saved) ? saved : 'dark';
    } catch {
      return 'dark';
    }
  });
  const [resolved, setResolved] = useState(() => computeResolved(preference));

  // Apply resolved theme to <html data-theme="...">
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-theme', resolved);
    // Also toggle tailwind's `dark` class for any native dark: utilities.
    if (resolved === 'dark') html.classList.add('dark');
    else html.classList.remove('dark');
    // Update theme-color meta for mobile status bar
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', resolved === 'dark' ? '#0B0F1A' : '#FFFFFF');
  }, [resolved]);

  // Recompute resolved when preference changes, and listen to system changes
  useEffect(() => {
    setResolved(computeResolved(preference));
    if (preference !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => setResolved(mq.matches ? 'light' : 'dark');
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [preference]);

  const setPreference = useCallback((value) => {
    if (!VALID.includes(value)) return;
    setPreferenceState(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, resolved, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
